import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getIn } from 'formik';

import { FilterDropDown } from '../../FilterDropdown/FilterDropdown';
import { GroupedCheckboxFilterMenu } from '../GroupedCheckboxFilterMenu/GroupedCheckboxFilterMenu';

import menuStyles from '../GroupedCheckboxFilterMenu/OrganizationDropdown.module.css';
import styles from './index.module.css';

const POPOVER_CLOSE_DEBOUNCE_MS = 1000;

const isSectionedOptions = (rawOptions) =>
  Array.isArray(rawOptions) &&
  rawOptions.length > 0 &&
  rawOptions.every(
    (o) =>
      o &&
      typeof o.title === 'string' &&
      Array.isArray(o.children) &&
      o.value === undefined,
  );

const isGroupedOptions = (rawOptions) =>
  rawOptions.some(
    (option) =>
      Array.isArray(option?.children) && option.children.some((child) => Array.isArray(child?.children)),
  );

const normalizeOptions = (rawOptions) => {
  if (!rawOptions?.length) {
    return [{ key: 'empty', sectionTitle: null, items: [], showTrailingArrow: true }];
  }

  if (isSectionedOptions(rawOptions)) {
    return rawOptions.map((section) => ({
      key: section.key || section.title,
      sectionTitle: section.title,
      items: section.children || [],
      showTrailingArrow: section.showNestedArrow !== false,
    }));
  }

  if (isGroupedOptions(rawOptions)) {
    return rawOptions.map((option) => ({
      key: option?.key || option?.title,
      sectionTitle: option?.title,
      items: option?.children || [],
      showTrailingArrow: true,
    }));
  }

  return [{ key: 'all', sectionTitle: null, items: rawOptions, showTrailingArrow: true }];
};

export const NestedCheckboxDropDown = ({
  Component,
  name,
  options,
  values,
  setFieldValue,
  shouldRenderSorting,
}) => {
  const [visible, setVisible] = useState(false);
  const closePopoverTimerRef = useRef(null);
  const pointerDownInSubPanelRef = useRef(false);

  const sections = useMemo(() => normalizeOptions(options), [options]);

  const menuSections = useMemo(
    () =>
      sections.map((section) => ({
        id: section.key,
        title: section.sectionTitle,
        items: section.items,
        showTrailingArrow: section.showTrailingArrow,
      })),
    [sections],
  );

  const selectedItems = getIn(values, name) || [];

  const handleVisibleChange = useCallback((open) => {
    if (open) {
      if (closePopoverTimerRef.current) {
        clearTimeout(closePopoverTimerRef.current);
        closePopoverTimerRef.current = null;
      }
      setVisible(true);
      return;
    }
    // Clicks on the portaled clinic panel can briefly report “close”; keep the filter open.
    if (pointerDownInSubPanelRef.current) {
      pointerDownInSubPanelRef.current = false;
      return;
    }
    if (closePopoverTimerRef.current) {
      clearTimeout(closePopoverTimerRef.current);
    }
    closePopoverTimerRef.current = setTimeout(() => {
      setVisible(false);
      closePopoverTimerRef.current = null;
    }, POPOVER_CLOSE_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (closePopoverTimerRef.current) {
        clearTimeout(closePopoverTimerRef.current);
        closePopoverTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!visible) return undefined;
    const onPointerDownCapture = (e) => {
      pointerDownInSubPanelRef.current = Boolean(e.target?.closest?.('[data-org-filter-sub-panel]'));
    };
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => document.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, [visible]);

  return (
    <th className='ant-table-cell'>
      <FilterDropDown
        visible={visible}
        content={
          <div data-nested-checkbox-filter-root onMouseDown={(e) => e.stopPropagation()}>
            <GroupedCheckboxFilterMenu
              sections={menuSections}
              selectedItems={selectedItems}
              onChange={(next) => setFieldValue(name, next)}
              fieldName={name}
              checkboxClassName={menuStyles.menuCheckbox}
            />
          </div>
        }
        overlayClassName={styles.nestedOverlay}
        name={name}
        values={values}
        setFieldValue={setFieldValue}
        shouldRenderSorting={shouldRenderSorting}
        onVisibleChange={handleVisibleChange}>
        <Component visible={visible} />
      </FilterDropDown>
    </th>
  );
};

NestedCheckboxDropDown.propTypes = {
  Component: PropTypes.elementType,
  name: PropTypes.string,
  options: PropTypes.array,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  shouldRenderSorting: PropTypes.bool,
};
